import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
  InputAdornment,
  Chip,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  ArrowUpward as ArrowUpIcon,
} from '@mui/icons-material';
import patternService from '../services/pattern.service';
import patternContainerService from '../services/patternContainerService';
import { ExercisePattern, PatternContainer } from '../types';
import { toast } from 'react-toastify';

interface PatternSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (pattern: ExercisePattern) => void;
}

const PatternSelectionDialog: React.FC<PatternSelectionDialogProps> = ({
  open,
  onClose,
  onSelect
}) => {
  const [patterns, setPatterns] = useState<ExercisePattern[]>([]);
  const [containers, setContainers] = useState<PatternContainer[]>([]);
  const [currentContainer, setCurrentContainer] = useState<PatternContainer | null>(null);
  const [navigationPath, setNavigationPath] = useState<PatternContainer[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [allPatterns, setAllPatterns] = useState<ExercisePattern[]>([]);
  const [allContainers, setAllContainers] = useState<PatternContainer[]>([]);

  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      // Load all patterns and containers for searching
      const [patternsData, containersData] = await Promise.all([
        patternService.getAllPatterns(),
        patternContainerService.getContainers(null)
      ]);

      setAllPatterns(Array.isArray(patternsData) ? patternsData : []);
      setAllContainers(Array.isArray(containersData) ? containersData : []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);

      if (currentContainer) {
        const contents = await patternContainerService.getContainerContents(currentContainer.id);
        setContainers(Array.isArray(contents?.containers) ? contents.containers : []);
        setPatterns(Array.isArray(contents?.patterns) ? contents.patterns : []);
      } else {
        const [containerData, patternData] = await Promise.all([
          patternContainerService.getContainers(null),
          patternService.getPatterns(null)
        ]);

        const validContainerData = Array.isArray(containerData) ? containerData : [];
        const validPatternData = Array.isArray(patternData) ? patternData : [];
        const rootPatterns = validPatternData.filter(p => !p.container);

        setContainers(validContainerData);
        setPatterns(rootPatterns);
      }
    } catch (error) {
      console.error('Error loading items:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [currentContainer]);

  const filterItems = useCallback(() => {
    if (!searchQuery.trim()) {
      loadItems();
      return;
    }

    const query = searchQuery.toLowerCase();

    // Function to get all parent containers of a pattern
    const getPatternContainerPath = (pattern: ExercisePattern): PatternContainer[] => {
      const path: PatternContainer[] = [];
      let currentId = pattern.container;

      while (currentId) {
        // eslint-disable-next-line no-loop-func
        const container = allContainers.find(c => c.id === currentId);
        if (!container) break;
        path.unshift(container);
        currentId = container.parent;
      }

      return path;
    };

    // Filter patterns: match if pattern title contains query OR any parent container title contains query
    const filteredPatterns = allPatterns.filter(pattern => {
      if (pattern.title.toLowerCase().includes(query)) {
        return true;
      }

      const containerPath = getPatternContainerPath(pattern);
      return containerPath.some(container => container.title.toLowerCase().includes(query));
    });

    // Filter containers: match if container title contains query OR has matching patterns inside
    const filteredContainers = allContainers.filter(container => {
      if (container.title.toLowerCase().includes(query)) {
        return true;
      }

      // Check if container has any matching patterns (recursively)
      const hasMatchingPatterns = allPatterns.some(pattern => {
        if (!pattern.title.toLowerCase().includes(query)) return false;

        const containerPath = getPatternContainerPath(pattern);
        return containerPath.some(c => c.id === container.id);
      });

      return hasMatchingPatterns;
    });

    setPatterns(filteredPatterns);
    setContainers(filteredContainers);
  }, [searchQuery, allPatterns, allContainers, loadItems]);

  // Effects
  useEffect(() => {
    if (open) {
      loadAllData();
    }
  }, [open, loadAllData]);

  useEffect(() => {
    if (open && !searchQuery) {
      loadItems();
    }
  }, [currentContainer, open, searchQuery, loadItems]);

  useEffect(() => {
    if (open && searchQuery) {
      filterItems();
    }
  }, [searchQuery, open, filterItems]);

  const handleContainerClick = async (container: PatternContainer) => {
    setCurrentContainer(container);
    setNavigationPath([...navigationPath, container]);
    setSearchQuery(''); // Clear search when navigating
  };

  const handleBackClick = () => {
    if (navigationPath.length > 0) {
      const newPath = [...navigationPath];
      newPath.pop();
      setNavigationPath(newPath);
      setCurrentContainer(newPath.length > 0 ? newPath[newPath.length - 1] : null);
      setSearchQuery(''); // Clear search when navigating
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      setNavigationPath([]);
      setCurrentContainer(null);
    } else {
      const newPath = navigationPath.slice(0, index + 1);
      setNavigationPath(newPath);
      setCurrentContainer(newPath[index]);
    }
    setSearchQuery(''); // Clear search when navigating
  };

  const handlePatternSelect = (pattern: ExercisePattern) => {
    onSelect(pattern);
    onClose();
    // Reset state
    setSearchQuery('');
    setCurrentContainer(null);
    setNavigationPath([]);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Sélectionner un pattern d'exercice</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Search Field */}
          <TextField
            fullWidth
            placeholder="Rechercher un pattern ou un dossier..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Breadcrumbs */}
          {!searchQuery && navigationPath.length > 0 && (
            <Breadcrumbs>
              <Link
                component="button"
                variant="body2"
                onClick={() => handleBreadcrumbClick(-1)}
                sx={{ cursor: 'pointer' }}
              >
                Racine
              </Link>
              {navigationPath.map((container, index) => (
                <Link
                  key={container.id}
                  component="button"
                  variant="body2"
                  onClick={() => handleBreadcrumbClick(index)}
                  sx={{ cursor: 'pointer' }}
                  color={index === navigationPath.length - 1 ? 'text.primary' : 'inherit'}
                >
                  {container.title}
                </Link>
              ))}
            </Breadcrumbs>
          )}

          {/* Results info */}
          {searchQuery && (
            <Typography variant="caption" color="text.secondary">
              {patterns.length + containers.length} résultat(s) trouvé(s)
            </Typography>
          )}

          {/* List */}
          <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <List>
                {/* Back button (only when not searching and not at root) */}
                {!searchQuery && currentContainer && (
                  <ListItem disablePadding>
                    <ListItemButton onClick={handleBackClick}>
                      <ArrowUpIcon sx={{ mr: 2, color: 'action.active' }} />
                      <ListItemText primary=".." />
                    </ListItemButton>
                  </ListItem>
                )}

                {/* Containers */}
                {containers.map((container) => (
                  <ListItem key={`container-${container.id}`} disablePadding>
                    <ListItemButton onClick={() => handleContainerClick(container)}>
                      <FolderIcon sx={{ mr: 2, color: 'primary.main' }} />
                      <ListItemText
                        primary={container.title}
                        primaryTypographyProps={{ fontWeight: 'bold' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}

                {/* Patterns */}
                {patterns.map((pattern) => (
                  <ListItem key={`pattern-${pattern.id}`} disablePadding>
                    <ListItemButton onClick={() => handlePatternSelect(pattern)}>
                      <FileIcon sx={{ mr: 2, color: 'action.active' }} />
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            {pattern.title}
                            {pattern.is_reusable && (
                              <Chip label="Réitérable" size="small" color="primary" />
                            )}
                          </Box>
                        }
                        secondary={`${pattern.exercise_count || 0} exercices générés`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}

                {/* Empty state */}
                {!loading && patterns.length === 0 && containers.length === 0 && (
                  <Box p={4} textAlign="center">
                    <Typography variant="body2" color="text.secondary">
                      {searchQuery
                        ? 'Aucun résultat trouvé'
                        : 'Aucun pattern disponible dans ce dossier'
                      }
                    </Typography>
                  </Box>
                )}
              </List>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatternSelectionDialog;
